import { RevisionLicenseConclusionScope } from '../../../../shared/model-typings/revisionlicenseconclusion';
import queryClient from '../../../ReactQuery/client';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { remove } from '../../../redux/api';
import { nuc } from '../../../utils';
import { getRevisionConclusionsQueryKey } from '../correctionsV2/useGetRevisionConclusions';
import { getBaseDependencyFetchQueryKey } from '../dependencies/queryKey';
import { getSnippetsBaseQueryKey } from '../snippets/shared';

type DeleteScopedConclusionParams = {
  dependencyRevisionLocator: string;
  scope: RevisionLicenseConclusionScope;
  scopeId: string;
};

const deleteScopedConclusion = async ({ dependencyRevisionLocator, scope, scopeId }: DeleteScopedConclusionParams) => {
  return remove(`/api/license-conclusions/${nuc(dependencyRevisionLocator)}`, { body: { scope, scopeId }, json: true });
};

/**
 * Hook for deleting a scoped license conclusion. Note that this does not support global or base conclusions.
 *
 * @param dependencyRevisionLocator The dependency revision locator.
 * @param userRevisionLocator Optional user revision locator - used to reset snippet queries
 */
export const useDeleteScopedConclusion = (dependencyRevisionLocator: string, userRevisionLocator?: string) => {
  return useToastedMutation({
    mutationFn: ({ scope, scopeId }: DeleteScopedConclusionParams) =>
      deleteScopedConclusion({ dependencyRevisionLocator, scope, scopeId }),
    successToastMessage: (_data, { scope }) =>
      scope === RevisionLicenseConclusionScope.ORGANIZATION
        ? 'Conclusion reverted across your organization.'
        : 'License conclusions successfully reverted.',
    errorToastMessage: 'Failed to revert license conclusions.',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getBaseDependencyFetchQueryKey(dependencyRevisionLocator) });
      // The project licenses page reads its banner through the revision conclusions query.
      void queryClient.invalidateQueries({ queryKey: getRevisionConclusionsQueryKey(dependencyRevisionLocator) });

      if (userRevisionLocator) {
        queryClient.invalidateQueries({ queryKey: getSnippetsBaseQueryKey(userRevisionLocator) });
      }
    },
  });
};
